// AIHistorySidebar.js — Daftar riwayat chat di sisi kiri AIPanel saat maximize.
//
// Cakupan sengaja sempit: LIST + RENAME + LANJUTKAN. Tidak ada hapus — halaman
// AI Chat History (/dashboard/ai-history) tetap satu-satunya tempat menghapus,
// supaya aksi merusak tidak berada satu klik dari daftar yang dipakai sehari-hari.
//
// ── Judul ────────────────────────────────────────────────────────────────────
// Judul yang ditampilkan berasal dari dua sumber, dengan urutan:
//   1. `title` — override manual dari tabel ai_session_meta (hasil rename)
//   2. `first_question` — pesan user pertama, dipotong; DIHITUNG SAAT BACA
//
// Judul otomatis tidak pernah ditulis ke database. Konsekuensinya yang
// diinginkan: seluruh sesi lama langsung punya judul tanpa migrasi apa pun, dan
// judul ikut berubah bila pertanyaan pertama di-edit. Mengosongkan kolom rename
// mengembalikan sesi ke judul otomatis.
//
// ── Scope ────────────────────────────────────────────────────────────────────
// Daftar difilter per halaman lewat `?dashboard=<nama tampilan>`. Nilai itu
// harus PERSIS sama dengan yang ditulis AIChat ke kolom `dashboard`, karena itu
// AIPanel menghitungnya lewat getDashboardContext() yang di-export AIChat —
// bukan dari label rute mentah.
//
// Scope per-user ditegakkan di SERVER (non-admin selalu dipaksa ke username
// sendiri). Tidak ada filter user di sini: menambahkannya di frontend hanya
// akan menyembunyikan baris, bukan mengamankannya.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Search, Plus, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

const PAGE_SIZE = 60;
const TITLE_MAX = 120;

// String lokal, tidak lewat translations.js. Alasannya: build gate
// scripts/check-i18n.js menuntut paritas penuh id/en/ar untuk setiap key, dan
// menambah ~10 key ke tiga tempat di file 8900 baris demi satu komponen adalah
// permukaan galat yang lebih besar daripada tabel kecil ini. Header AIChat
// sudah memakai pola yang sama ('Kecilkan', 'Perbesar 1 halaman').
const L = {
  id: {
    history: 'Riwayat', newChat: 'Chat baru', search: 'Cari riwayat…',
    empty: 'Belum ada riwayat di halaman ini.', emptySearch: 'Tidak ada yang cocok.',
    rename: 'Ubah judul', save: 'Simpan', cancel: 'Batal',
    untitled: '(tanpa judul)', msgs: 'pesan', failed: 'Gagal memuat riwayat.',
    renameFailed: 'Gagal menyimpan judul',
    del: 'Hapus sesi', confirmDel: 'Hapus sesi ini?', delFailed: 'Gagal menghapus sesi',
  },
  en: {
    history: 'History', newChat: 'New chat', search: 'Search history…',
    empty: 'No history on this page yet.', emptySearch: 'No matches.',
    rename: 'Rename', save: 'Save', cancel: 'Cancel',
    untitled: '(untitled)', msgs: 'messages', failed: 'Failed to load history.',
    renameFailed: 'Failed to save title',
    del: 'Delete session', confirmDel: 'Delete this session?', delFailed: 'Failed to delete session',
  },
  ar: {
    history: 'السجل', newChat: 'محادثة جديدة', search: '…بحث في السجل',
    empty: 'لا يوجد سجل في هذه الصفحة بعد.', emptySearch: 'لا توجد نتائج.',
    rename: 'إعادة تسمية', save: 'حفظ', cancel: 'إلغاء',
    untitled: '(بدون عنوان)', msgs: 'رسائل', failed: 'تعذّر تحميل السجل.',
    renameFailed: 'تعذّر حفظ العنوان',
    del: 'حذف الجلسة', confirmDel: 'حذف هذه الجلسة؟', delFailed: 'تعذّر حذف الجلسة',
  },
};

// Judul otomatis dari pertanyaan pertama. Newline diratakan lebih dulu: tanpa
// itu satu pertanyaan multi-baris memotong pada karakter tak terlihat dan hasil
// potongannya tampak sewenang-wenang.
function autoTitle(s) {
  const flat = String(s || '').replace(/\s+/g, ' ').trim();
  if (!flat) return '';
  return flat.length > 64 ? flat.slice(0, 64).trimEnd() + '…' : flat;
}

function timeAgo(iso, lang) {
  if (!iso) return '';
  // created_at ditulis SQLite sebagai "YYYY-MM-DD HH:MM:SS" dalam UTC tanpa
  // penanda zona. Date.parse() memperlakukan bentuk itu sebagai waktu LOKAL di
  // sebagian browser, sehingga selisihnya meleset 7 jam di WIB dan semua item
  // tampak "baru saja". Ditambahkan 'Z' secara eksplisit.
  const norm = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(iso)
    ? iso.replace(' ', 'T') + 'Z'
    : iso;
  const ms = Date.now() - new Date(norm).getTime();
  if (!isFinite(ms)) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1)  return lang === 'en' ? 'just now' : lang === 'ar' ? 'الآن' : 'baru saja';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}j`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}h`;
  return new Date(norm).toLocaleDateString();
}

export default function AIHistorySidebar({
  dashboardName,     // nama tampilan dashboard; '' → sidebar tidak memfilter
  activeSessionId,   // sesi yang sedang dibuka di AIChat (untuk sorotan)
  refreshToken,      // angka; naik → muat ulang daftar
  onPick,            // (sessionId) => void
  onNewChat,         // () => void
  // (sessionId) => string | null. Bila mengembalikan URL, judul dibungkus <a>
  // sehingga klik kanan memunculkan "Open link in new tab" BAWAAN browser —
  // tidak ada menu konteks kustom. Ctrl+Click dan klik-tengah ikut jalan gratis.
  // Halaman tujuan WAJIB bisa memulihkan sesi dari query `?session=` (lihat
  // AICoding.js / AIWorkspace.js), kalau tidak tab baru cuma terbuka kosong.
  // Tanpa prop ini komponen berperilaku persis seperti sebelumnya.
  sessionHref,
  lang = 'id',
}) {
  const t = L[lang] || L.id;

  const [sessions, setSessions] = useState([]);
  // Konfirmasi INLINE, bukan window.confirm: penghapusan ini permanen (server
  // membuang seluruh baris ai_chat_history sesi itu, tidak ada soft-delete),
  // jadi satu klik tanpa jeda terlalu murah untuk aksi yang tak bisa dibatalkan.
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [debounced, setDebounced] = useState('');
  const [editId, setEditId]     = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving]     = useState(false);
  const editRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(async () => {
    // `alive` ditangani pemanggil lewat effect di bawah.
    const qs = new URLSearchParams({ limit: String(PAGE_SIZE), offset: '0' });
    if (dashboardName) qs.set('dashboard', dashboardName);
    if (debounced.trim()) qs.set('search', debounced.trim());
    const r = await api.get(`/api/ai/chat-history/sessions?${qs.toString()}`);
    return Array.isArray(r.data?.sessions) ? r.data.sessions : [];
  }, [dashboardName, debounced]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    load()
      .then(rows => { if (!alive) return; setSessions(rows); setError(''); setConfirmId(null); })
      // Kegagalan HARUS terlihat. Daftar kosong yang diam tidak bisa dibedakan
      // dari "memang belum ada riwayat", dan itu persis jenis kebingungan yang
      // sudah pernah terjadi pada /api/ai/sql-schema.
      .catch(e => {
        if (!alive) return;
        setSessions([]);
        setError(t.failed);
        console.error('[AIHistorySidebar] gagal memuat sesi:',
          e?.response?.status || '', e?.response?.data?.error || e.message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [load, refreshToken, t.failed]);

  useEffect(() => { if (editId) editRef.current?.focus(); }, [editId]);

  const startRename = (s, e) => {
    if (e) e.stopPropagation();
    setEditId(s.session_id);
    // Kolom rename dimulai dari judul yang SEDANG TERLIHAT, termasuk bila itu
    // judul otomatis. Memulai dari kosong memaksa mengetik ulang dari nol untuk
    // sekadar menyunting satu kata.
    setConfirmId(null);   // dua mode aksi pada satu baris tidak boleh tampil bersamaan
    setEditText(s.title || autoTitle(s.first_question));
  };

  const cancelRename = (e) => {
    if (e) e.stopPropagation();
    setEditId(null); setEditText('');
  };

  const commitRename = async (sessionId, e) => {
    if (e) e.stopPropagation();
    if (saving) return;
    const next = editText.replace(/\s+/g, ' ').trim().slice(0, TITLE_MAX);
    setSaving(true);
    try {
      const r = await api.put(
        `/api/ai/chat-history/session/${encodeURIComponent(sessionId)}/title`,
        { title: next }
      );
      // Nilai dari SERVER, bukan `next`: server yang meratakan spasi dan yang
      // memutuskan bahwa judul kosong berarti kembali ke judul otomatis.
      const saved = r.data?.title ?? '';
      setSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, title: saved } : s));
      setEditId(null); setEditText('');
    } catch (err) {
      alert(`${t.renameFailed}: ${err?.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sessionId, e) => {
    if (e) e.stopPropagation();
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/api/ai/chat-history/session/${encodeURIComponent(sessionId)}`);
      setSessions(prev => prev.filter(x => x.session_id !== sessionId));
      setConfirmId(null);
      // Sesi yang sedang DIBUKA baru saja lenyap dari server. Tanpa ini
      // transkripnya tetap tampil di layar dan pesan berikutnya akan menulis
      // ke session_id yang sudah tidak punya riwayat — sesi "hantu" yang
      // muncul lagi di daftar dengan isi terpotong.
      if (sessionId === activeSessionId) onNewChat?.();
    } catch (err) {
      alert(`${t.delFailed}: ${err?.response?.data?.error || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Muat PERTAMA (layar masih kosong) vs muat ULANG (daftar sudah ada).
  // Perbedaan ini yang menentukan apakah daftar boleh dilepas dari DOM —
  // lihat komentar di kontainer daftar.
  const firstLoad = loading && sessions.length === 0;

  const onEditKeyDown = (sessionId, e) => {
    if (e.key === 'Enter')  { e.preventDefault(); commitRename(sessionId, e); }
    if (e.key === 'Escape') { e.preventDefault(); cancelRename(e); }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50 border-r border-slate-200 min-h-0">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between gap-2 flex-shrink-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <MessageSquare style={{ width: 14, height: 14 }} className="text-slate-400 flex-shrink-0" />
          <span className="text-xs font-semibold text-slate-600 truncate">{t.history}</span>
          {/* Indikator segar-ulang pindah KE SINI. Sebelumnya satu-satunya
              penanda muat adalah spinner besar yang menggantikan daftar, dan
              itulah yang membuang posisi scroll. */}
          {loading && !firstLoad && (
            <Loader2 style={{ width: 11, height: 11 }}
                     className="animate-spin text-slate-300 flex-shrink-0" />
          )}
        </div>
        <button
          onClick={onNewChat}
          title={t.newChat}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium
                     text-brand-600 hover:bg-brand-50 transition-colors flex-shrink-0"
        >
          <Plus style={{ width: 13, height: 13 }} />
          <span>{t.newChat}</span>
        </button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 flex-shrink-0">
        <div className="relative">
          <Search
            style={{ width: 13, height: 13 }}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t.search}
            className="w-full pl-8 pr-2 py-1.5 text-xs rounded-lg border border-slate-200
                       bg-white focus:outline-none focus:ring-2 focus:ring-brand-200
                       focus:border-brand-300 transition-shadow"
          />
        </div>
      </div>

      {/* Daftar */}
      <div className="flex-1 overflow-y-auto min-h-0 px-2 pb-2">
        {/* Spinner besar HANYA pada muat pertama.
            Dulu syaratnya `loading` saja, sehingga setiap penyegaran melepas
            seluruh daftar dari DOM dan menggantinya dengan spinner setinggi
            ~40px. Tinggi konten runtuh → browser menjepit scrollTop ke 0 →
            saat daftar kembali, posisi scroll sudah hilang. Karena AIChat
            memanggil onSessionActivity (→ historyTick → refreshToken) setiap
            kali sesi dibuka atau pesan disimpan, memilih sesi di bagian bawah
            SELALU melempar daftar kembali ke atas.
            Dengan mempertahankan daftar lama selama penyegaran, React
            merekonsiliasi baris berdasarkan key session_id dan scroll bertahan
            sendirinya — tanpa perlu menyimpan/memulihkan scrollTop manual. */}
        {firstLoad && (
          <div className="flex items-center justify-center gap-2 py-6 text-slate-400">
            <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
            <span className="text-xs">…</span>
          </div>
        )}

        {!firstLoad && error && (
          <div className="px-2 py-4 text-xs text-red-500">{error}</div>
        )}

        {!loading && !error && sessions.length === 0 && (
          <div className="px-2 py-6 text-xs text-slate-400 text-center leading-relaxed">
            {debounced.trim() ? t.emptySearch : t.empty}
          </div>
        )}

        {!firstLoad && !error && sessions.map(s => {
          const isActive = s.session_id === activeSessionId;
          const isEdit   = s.session_id === editId;
          const shown    = s.title || autoTitle(s.first_question) || t.untitled;
          const href     = sessionHref ? sessionHref(s.session_id) : null;

          return (
            <div
              key={s.session_id}
              // Guard modifier WAJIB ada bersama <a> di bawah: Ctrl+Click tetap
              // memicu onClick React. Tanpa guard, satu Ctrl+Click akan membuka
              // tab baru DAN mengganti sesi di tab sekarang sekaligus.
              // Klik-tengah tidak memicu onClick sama sekali, jadi aman sendiri.
              onClick={(e) => {
                if (isEdit) return;
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
                onPick(s.session_id);
              }}
              className={`group rounded-lg px-2.5 py-2 mb-1 cursor-pointer transition-colors
                ${isActive
                  ? 'bg-brand-50 border border-brand-200'
                  : 'border border-transparent hover:bg-white hover:border-slate-200'}`}
            >
              {isEdit ? (
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <input
                    ref={editRef}
                    value={editText}
                    maxLength={TITLE_MAX}
                    onChange={e => setEditText(e.target.value)}
                    onKeyDown={e => onEditKeyDown(s.session_id, e)}
                    className="flex-1 min-w-0 px-1.5 py-1 text-xs rounded border border-brand-300
                               focus:outline-none focus:ring-2 focus:ring-brand-200"
                  />
                  <button
                    onClick={e => commitRename(s.session_id, e)}
                    disabled={saving}
                    title={t.save}
                    className="p-1 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
                  >
                    <Check style={{ width: 13, height: 13 }} />
                  </button>
                  <button
                    onClick={cancelRename}
                    title={t.cancel}
                    className="p-1 rounded text-slate-400 hover:bg-slate-100"
                  >
                    <X style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-1">
                    {/* Elemen judul: <a> bila halaman ini punya deep-link, <span>
                        bila tidak. Sengaja <a href> sungguhan, bukan div dengan
                        menu konteks buatan sendiri — hanya anchor asli yang
                        membuat "Open link in new tab" muncul di menu browser.
                        Klik kiri biasa tetap SPA (preventDefault → bubbling ke
                        onClick baris di atas), jadi tidak ada reload halaman. */}
                    {React.createElement(
                      href ? 'a' : 'span',
                      {
                        ...(href ? {
                          href,
                          onClick: (e) => {
                            // Modifier dilepas ke browser: preventDefault di sini
                            // justru akan MEMBATALKAN Ctrl+Click / buka tab baru.
                            if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
                            e.preventDefault();
                          },
                          draggable: false,
                        } : {}),
                        className: `flex-1 min-w-0 text-xs leading-snug break-words no-underline
                          ${isActive ? 'text-brand-700 font-semibold' : 'text-slate-700'}`,
                        style: {
                          display: '-webkit-box', WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical', overflow: 'hidden',
                          color: 'inherit', textDecoration: 'none',
                        },
                      },
                      shown
                    )}
                    {/* Tombol rename hanya muncul saat hover supaya daftar tetap
                        tenang; tetap dapat dijangkau keyboard karena ia tombol
                        sungguhan, bukan disembunyikan dengan display:none. */}
                    <button
                      onClick={e => startRename(s, e)}
                      title={t.rename}
                      className="p-0.5 rounded text-slate-300 opacity-0 group-hover:opacity-100
                                 hover:text-brand-600 hover:bg-white transition-opacity flex-shrink-0"
                    >
                      <Pencil style={{ width: 11, height: 11 }} />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setConfirmId(s.session_id); }}
                      title={t.del}
                      className="p-0.5 rounded text-slate-300 opacity-0 group-hover:opacity-100
                                 hover:text-red-600 hover:bg-white transition-opacity flex-shrink-0"
                    >
                      <Trash2 style={{ width: 11, height: 11 }} />
                    </button>
                  </div>
                  {confirmId === s.session_id && (
                    <div className="flex items-center gap-1.5 mt-1.5" onClick={e => e.stopPropagation()}>
                      <span className="flex-1 min-w-0 text-red-600" style={{ fontSize: 10 }}>
                        {t.confirmDel}
                      </span>
                      <button
                        onClick={e => handleDelete(s.session_id, e)}
                        disabled={deleting}
                        title={t.del}
                        className="px-1.5 py-0.5 rounded bg-red-600 text-white hover:bg-red-700
                                   disabled:opacity-40 flex-shrink-0"
                        style={{ fontSize: 10 }}
                      >
                        {deleting
                          ? <Loader2 className="animate-spin" style={{ width: 11, height: 11 }} />
                          : <Check style={{ width: 11, height: 11 }} />}
                      </button>
                      <button
                        onClick={() => setConfirmId(null)}
                        title={t.cancel}
                        className="px-1.5 py-0.5 rounded text-slate-400 hover:bg-slate-100 flex-shrink-0"
                        style={{ fontSize: 10 }}
                      >
                        <X style={{ width: 11, height: 11 }} />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-1 text-slate-400" style={{ fontSize: 10 }}>
                    <span>{timeAgo(s.last_at, lang)}</span>
                    <span>·</span>
                    <span>{s.message_count || 0} {t.msgs}</span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
